import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    HelpCircle,
    Mail,
    Home,
    Wrench,
    Users,
    FileText,
    Send,
    ChevronDown,
    Sparkles,
    Droplets
} from "lucide-react";
import GlassCard from "./GlassCard";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const HelpSupportSheet = ({ open, onOpenChange }) => {

    const features = [
        {
            id: "records",
            icon: Home,
            color: "text-blue-500",
            bg: "bg-blue-500/10",
            title: "Home Records",
            content: "Your centralized property dashboard. Log paint codes, appliance warranties, furniture inventory, and appliance service reminders, all linked with dynamic sub-tasks and services."
        },
        {
            id: "vault",
            icon: FileText,
            color: "text-emerald-500",
            bg: "bg-emerald-500/10",
            title: "Home Vault",
            content: "Securely store and organize house plans, compliance certificates, meter numbers, invoices, and manuals. Supports easy uploads of photos and PDFs."
        },
        {
            id: "directory",
            icon: Users,
            color: "text-purple-500",
            bg: "bg-purple-500/10",
            title: "Directory",
            content: "Access a directory of home service providers. Add your own trusted contractors, view verified professionals, and keep all property contacts in one place."
        },
        {
            id: "transfer",
            icon: Send,
            color: "text-amber-500",
            bg: "bg-amber-500/10",
            title: "Property Transfer",
            content: "Handing over your property? Bundle your entire home history—warranties, paint codes, manuals, and service logs—and transfer it digitally to the new owner."
        },
        {
            id: "assistant",
            icon: Sparkles,
            color: "text-violet-500",
            bg: "bg-violet-500/10",
            title: "Ask HomeEzi Assistant",
            content: "Ask questions about your paint colors, WiFi details, appliance service dates, and maintenance checklists."
        },
        {
            id: "service-reminders",
            icon: Wrench,
            color: "text-sky-500",
            bg: "bg-sky-500/10",
            title: "Appliance Service Reminders",
            content: "Keep track of active maintenance schedules with automated service reminders, warranty tracking, and custom sub-task checklists for appliances and services."
        }
    ];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[90vh] rounded-t-3xl flex flex-col pt-6"
                style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 text-left">
                    <SheetTitle className="text-xl font-bold flex items-center gap-2">
                        <HelpCircle className="w-5 h-5 text-primary" />
                        Help & Support
                    </SheetTitle>
                    <SheetDescription>
                        Learn how to get the most out of HomeEzi.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto scrollbar-hide pb-6" style={{ overscrollBehavior: 'contain' }}>
                    <div className="space-y-6">
                        {/* Intro */}
                        <div className="bg-primary/5 p-4 rounded-2xl border border-primary/10">
                            <p className="text-sm font-medium text-foreground leading-relaxed">
                                Welcome to your property's digital twin. Below is a quick guide to the core features designed to make home ownership effortless.
                            </p>
                        </div>

                        {/* Features Accordion */}
                        <div className="space-y-2">
                            <h3 className="text-sm font-semibold text-muted-foreground ml-1">App Features</h3>
                            <Accordion type="single" collapsible className="w-full space-y-2">
                                {features.map((feature) => (
                                    <AccordionItem key={feature.id} value={feature.id} className="border-none">
                                        <GlassCard className="px-0 py-0 overflow-hidden">
                                            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-white/5 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg ${feature.bg}`}>
                                                        <feature.icon className={`w-4 h-4 ${feature.color}`} />
                                                    </div>
                                                    <span className="font-semibold text-foreground">{feature.title}</span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-4 pb-4 pt-0 text-muted-foreground leading-relaxed">
                                                <div className="pl-[3.25rem]">
                                                    {feature.content}
                                                </div>
                                            </AccordionContent>
                                        </GlassCard>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>

                        {/* Contact Section */}
                        <div className="space-y-3 pt-4">
                            <h3 className="text-sm font-semibold text-muted-foreground ml-1">Contact Support</h3>
                            <GlassCard className="p-5">
                                <div className="text-center space-y-4">
                                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto text-primary">
                                        <Mail className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-foreground">Have a question?</h4>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            We're here to help. Reach out directly for assistance or feedback.
                                        </p>
                                    </div>
                                    <Button asChild className="w-full rounded-xl" variant="outline">
                                        <a href="mailto:support@homeezi.co.za">
                                            Email Support
                                        </a>
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        support@homeezi.co.za
                                    </p>
                                </div>
                            </GlassCard>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default HelpSupportSheet;
